/**
 * Admin-configurable invoice line-item COLUMNS.
 *
 * Different customers want different columns on the printed invoice — some
 * need Remarks / Packs, others don't. The admin toggles the OPTIONAL columns
 * here (Settings → Print Formats) and every invoice/order/note print honours
 * the choice. The mandatory columns (Sr, Description, Qty, Rate, Taxable) are
 * always shown and not configurable.
 *
 * Stored in tenant-scoped localStorage like the rest of the print settings;
 * server-side per-tenant persistence is a backend gap (G84/§70 family).
 */

export type InvoiceColumnKey =
  | 'remarks'
  | 'hsnCode'
  | 'unit'
  | 'packs'
  | 'packType'
  | 'tradeDiscount';

export interface InvoiceColumnDef {
  key: InvoiceColumnKey;
  label: string;
  /** Default visibility — Remarks/Packs/Pack off by default (the common ask). */
  default: boolean;
}

/** The optional, admin-toggleable columns (mandatory ones are never listed). */
export const INVOICE_COLUMNS: InvoiceColumnDef[] = [
  { key: 'hsnCode', label: 'HSN / SAC', default: true },
  { key: 'unit', label: 'Unit', default: true },
  { key: 'tradeDiscount', label: 'Trade Discount', default: true },
  { key: 'remarks', label: 'Remarks', default: false },
  { key: 'packs', label: 'No. of Packs', default: false },
  { key: 'packType', label: 'Pack Type', default: false },
];

export type InvoiceColumnConfig = Record<InvoiceColumnKey, boolean>;

const LS_KEY = 'kss-invoice-columns';

function defaults(): InvoiceColumnConfig {
  const o = {} as InvoiceColumnConfig;
  for (const c of INVOICE_COLUMNS) o[c.key] = c.default;
  return o;
}

/** Effective column config: stored overrides merged over the defaults. */
export function getInvoiceColumns(): InvoiceColumnConfig {
  const base = defaults();
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<InvoiceColumnConfig>;
      for (const c of INVOICE_COLUMNS) {
        if (typeof saved[c.key] === 'boolean') base[c.key] = saved[c.key] as boolean;
      }
    }
  } catch { /* fall back to defaults */ }
  return base;
}

export function setInvoiceColumns(next: InvoiceColumnConfig): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* storage disabled */ }
}

export function resetInvoiceColumns(): void {
  try { localStorage.removeItem(LS_KEY); } catch { /* */ }
}
